/** @format */
/* eslint-disable */
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import LocaleData from 'dayjs/plugin/localeData';
import RelativeTime from 'dayjs/plugin/relativeTime';
import AdvancedFormat from 'dayjs/plugin/advancedFormat';
import Time from './time';
import RangeMode from './rangeMode';
import {numberFormatCode} from '@/js/modules/csI18n';
import $cs_ from '@/js/plugins/cs-underscore-helpers';

dayjs.extend(AdvancedFormat);
dayjs.extend(LocalizedFormat);
dayjs.extend(LocaleData);
dayjs.extend(RelativeTime);

// TODO: consider adding the utc plugin to work with dates in utc format
// see: https://day.js.org/docs/en/parse/utc & https://day.js.org/docs/en/plugin/utc

const $_dayPrivate = {
    enDaysListShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    /**
     *
     * @param {Object} date the dayjs date object
     * @returns {Integer} of the year 2020
     */
    getYearOfDate(date) {
		return parseInt(date.format('YYYY'));
    },
    /**
     * @description
     * We are just looking to get an index of a day so we will use
     * the en locale for that so that we can work with our `enDaysListShort`
     * array
     *
     * @param {Object} dateObject the dayjs date object
     *
     * @returns {Integer} the index of the current date day ie Sun == 0 etc
     */
    normalisedIndex(dateObject) {
		const enDayRef = dateObject.locale('en').format('ddd');
		return $_dayPrivate.enDaysListShort.findIndex((day) => day.toLowerCase() === enDayRef.toLowerCase());
    },

    /**
     * Returns 1st, 2nd, 3rd etc
     *
     * @param {Integer} n
     */
    getNumberWithOrdinal(n) {
		let s = ['th', 'st', 'nd', 'rd'],
			v = n % 100;
		return n + (s[(v - 20) % 10] || s[v] || s[0]);
    },

    /**
     * @description
     * Establish whether the date in question can be selected based on varying criteria
     *
     * @param {Object} settings
     *
     * @returns {Boolean}
     */
    dayIsSelectable({ jsDateRef, settings, isWeekday }) {
		if (!isWeekday && settings.showWeekdaysOnly) return false;
		if (settings.disabledDates.length > 0) {
			if (settings.disabledDates.find((d) => d.jsDateRef === jsDateRef)) return false;
		}

		let valid = {
			min: true,
			max: true
		};

		const yearOfDate = $_dayPrivate.getYearOfDate($_dayPublic.getDayJsDateObject(jsDateRef, settings));
		const minDate = settings.minDate;
		
		if (minDate && minDate.date && minDate.date.jsDateRef) {
			if (minDate.date.jsDateRef != jsDateRef) {
				valid.min = $_dayPublic.isBefore(minDate.date.jsDateRef, jsDateRef);
			}
		} else if (settings.minYear != undefined) {
			valid.min = settings.minYear <= yearOfDate;
		}

		const maxDate = settings.maxDate;
		if (maxDate && maxDate.date && maxDate.date.jsDateRef) {
			if (maxDate.date.jsDateRef != jsDateRef) {
				valid.max = $_dayPublic.isAfter(maxDate.date.jsDateRef, jsDateRef);
			}
		} else if (settings.maxYear != undefined) {
			valid.max = settings.maxYear >= yearOfDate;
		}
		return valid.min && valid.max;
    },

    /**
     * Takes a list of days by local and
     * returns an array of objects with the day and their index in them
     *
     * @param {Object} settings
     * @returns {Object}
     */
    listByLocale(settings) {
		let weekDays = [];
		for (let i = 0; i < 7; i++) {
			const incremented = dayjs().add(i, 'day');
			weekDays.push({
				byLocale: incremented.locale(settings.locale).format('ddd'),
				byEn: incremented.locale('en').format('ddd'),
				dayIndex: $_dayPrivate.normalisedIndex(incremented)
			});
		}

		return $_dayPrivate.enDaysListShort.map((day) => {
			const target = weekDays.find((d) => d.byEn.toLowerCase() === day.toLowerCase());

			return {
				day: target.byLocale,
				dayIndex: target.dayIndex
			};
		});
    },
}

const $_dayPublic = {
    /**
     * @public
     * @description
     * Take a date string, call dayJS and return a consistent object across the platform for
     * use
     *
     * @param {String} dateToSet yyyy-mm-dd
     * @param {Object} instance - the incoming datepicker instance
     *
     * @return {Object}
     */
	getDateObjectFromDateString(dateToSet, instance) {
        const date = $_dayPublic.getDayJsDateObject(dateToSet, instance);
        return $_dayPublic.getDateObject(date, instance);
	},

    /**
     * @public
     * @description
     * This is the bread and butter date and time creation object that
     * essentially every date use in the whole application will refer to.
     *
     * @param {Object} date the transformed dayJS date object that we will now decorate
     * @param {Object} settings - this is the current datepicker instance
     */
    getDateObject(date, settings) {
		const year = $_dayPrivate.getYearOfDate(date);
		const yearByLocale = new Intl.NumberFormat(numberFormatCode()).format(parseInt(year)).replace(/,/g,"");
        const dayIndex = $_dayPrivate.normalisedIndex(date);

        //we will store a string ref that we can use to call new date objects from
		const jsDateRef = `${year}-${date.format('MM')}-${date.format('DD')}`;
        const day = parseInt(date.format('D'));
		const monthLong = date.format('MMMM');
		const isWeekday = dayIndex !== 0 && dayIndex !== 6;
		const dateObj = {
			humanReadable: `${date.format('ddd')}, ${$_dayPrivate.getNumberWithOrdinal(day)} ${date.format('MMM')} ${yearByLocale}`,
			dayIndex,
			day,
			monthLong,
            year,
            yearByLocale,
            monthIntAsString: date.format('MM'), // eg "01" instead of "1" - used when we need to build new date strings.
			monthInt: parseInt(date.format('MM')),
			dateString: date.format(settings.outputFormat), //this is what we set to the users preferred format
			jsDateRef,
			isWeekday,
			isSelectable: $_dayPrivate.dayIsSelectable({ jsDateRef, settings, isWeekday })
        };

		return {
			date: dateObj,
			time: Time.timeFromDate(date)
		};
    },

    /**
     * @public
     * @description
     * This method allows us to reassign dates but preserve whatever time was associated to the
     * particular selected date.
     *
     * @param {Object} instance
     * @param {Array} addedDates
     */
    recacheDates(instance, addedDates){
        //doing these to be able to access the time later
        const cachedDates = JSON.parse(JSON.stringify(instance.selectedDates));

        const newDates = [];
        for (let i = 0; i <= addedDates.length - 1; i++) {
            const d = addedDates[i];
            if (d && cachedDates[i]) {
                d.time = cachedDates[i].time; //override the time for the given position here
            }
            newDates.push(d);
        }

        return newDates;
    },

    /**
     * @public
     * @description
     * Gets the days of the week by locale and rearranges them according to whatever we wish the
     * first date of the week to be.
     *
     * @param {Object} instance - the datepicker instance
     * @returns {Array}
     */
    orderedDays(instance) {
		return [...$_dayPrivate.listByLocale(instance).splice(instance.firstDayOfWeek), ...$_dayPrivate.listByLocale(instance).splice(0, instance.firstDayOfWeek)];
    },

    /**
     * @public
     * @description
     * Establishes whether a day should be displayed in the calendar
     * ie weekdays only in showWeekdaysOnly mode
     *
     * @param {Integer} dayIndex
     * @param {Object} instance
     *
     * @returns {Boolean}
     */
    shouldDisplay(dayIndex, instance) {
		if (instance.showWeekdaysOnly === true) {
			return dayIndex !== 0 && dayIndex !== 6;
		}
		return true;
    },

    /**
     * @public
     * @description
     * Build up an object of applicable classes for css based on particulars
     * for the individual date
     *
     * @param {Object} monthDate the active date we are looking at
     * @param {String} calendarMode range or standard
     * @param {Array} selectedDates dates we've already chosen
     * @param {Object} instance the active datepicker instance
     *
     * @return {Object} of classes
     */
    dateCellClasses(monthDate, calendarMode, selectedDates, instance) {
		const todaysDate = $cs_.get(instance.today, 'date', 'jsDateRef');
		const classes = {
			'lc-date-is-today': monthDate.date.jsDateRef === todaysDate,
			'lc-selected-date': selectedDates.some((d) => d.date.dateString === monthDate.date.dateString),
			'lc-date-within-range': RangeMode.isDateInAvailableRange(monthDate, calendarMode, selectedDates, instance),
            'lc-is-selectable': monthDate.date.isSelectable,
            'lc-active-selected-date': (selectedDates.length > 1) ? instance.activeSelectedDate && instance.activeSelectedDate.date.jsDateRef != undefined && instance.activeSelectedDate.date.jsDateRef === monthDate.date.jsDateRef : false,
			'lc-readonly-active': instance.readOnlyMode,
			'lc-current-hover-cell': instance.hoveredDate != undefined ? instance.hoveredDate.date.jsDateRef === monthDate.date.jsDateRef : false,
			'lc-current-keyevent-cell': instance.keyFocusDate != undefined ? instance.keyFocusDate.date.jsDateRef === monthDate.date.jsDateRef : false,
            'lc-not-selectable': !monthDate.date.isSelectable,
            'lc-month-next': instance.display.monthIntAsString != monthDate.date.monthIntAsString,
            'lc-month-previous': instance.display.monthIntAsString != monthDate.date.monthIntAsString
        };
        if (calendarMode == 'range') {
            if(selectedDates.length == 2) {
                classes['lc-date-range-start'] = selectedDates[0].date.dateString === monthDate.date.dateString
                classes['lc-date-range-end'] = selectedDates[1].date.dateString === monthDate.date.dateString
            }
        }
        classes['lc-datepicker-hover-style'] = false;
        if (instance.hoveredDate != undefined && monthDate.date.jsDateRef === instance.hoveredDate.date.jsDateRef && !classes['lc-selected-date']) {
            //if we've hovered over but not selected this date, apply our theme class or the default
            classes['lc-datepicker-hover-style'] = true;
        }
        return classes;
    },

    /**
     * @public
     * @description
     * Checks to see if the given date 1 happens before the given date 2
     *
     * @param {Object} date1 - eg 2020-01-01
     * @param {Object} date2 - eg 2020-01-02
     *
     * returns {Boolean}
     */
    isBefore(date1, date2) {
        return dayjs(date1).isBefore(dayjs(date2));
    },

    /**
     * @public
     * @description
     * Checks to see if the given date 1 happens after the given date 2
     *
     * @param {Object} date1 - eg 2020-01-01
     * @param {Object} date2 - eg 2020-01-02
     *
     * returns {Boolean}
     */
    isAfter(date1, date2) {
        return dayjs(date1).isAfter(dayjs(date2));
    },

	/**
     * @description
     * Convert the datestring to a dayJs object and return it if its valid
     * 
     * @param {String} dateToSet 
     * @param {Object} settings destructured instance settings
	 * 
	 * @returns {Object} - empty if nothing valid found
     */
	getDayJsDateObject(dateToSet, { locale }) {
		const getDate = dayjs(dateToSet).locale(locale);
		if (getDate.isValid()) return getDate;
		return {};
		//console.error(`${dateToSet} is not a valid date`); //use if needed for debugginf
    },

	/**
     * @description
     * We need to load the locale data locally before returning the instance and 
	 * getting formatted dates etc by locale.
     * 
     * @param {String} locale 
	 * 
	 * @returns {Promise} - 
     */
	getLocalDayJsObject(locale) {
		return new Promise((resolve, reject) => {
			return import(/* webpackChunkName: "locale" */ `dayjs/locale/${locale}.js`)
			.then((localSettings) => {
				dayjs.locale(locale);
				return resolve({dayjs, localSettings});
			})
			.catch((error) => {
				return reject(error);
			});
		});
		
		
		//console.error(`${dateToSet} is not a valid date`); //use if needed for debugginf
    }
};

export {$_dayPublic as default};
